/**
 * HomePage - Landing Page
 *
 * Migrated to BasePage architecture.
 * Demonstrates: Landing page with search form, features grid, philosophy section, animated blobs.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  BadgeCheck,
  Banknote,
  Building,
  Calendar,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  HeartHandshake,
  Home,
  Map,
  MapPin,
  Search,
  Shield,
  Sparkles,
  Users,
} from "lucide-react";

// Hooks & Contexts
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";
import { useTranslation } from "@/shared/hooks";
import { useGeoLocations } from "@/features/geo/hooks";

// New Design System
import { BasePage } from "@/shared/ui/layout/BasePage";
import { Box, Text } from "@/shared/ui/base";
import { Card, Button, Chip } from "@/shared/ui/components";

// Components
import { Select, LanguageSwitcher } from "@/shared/ui/components";

// Constants
import { AppRoutes, OCCUPANT_OPTIONS, ROOM_LAYOUTS } from "@/shared/constants";

// Types

export const HomePage = observer(function HomePage() {
  const t = useTranslation();
  const { authStore } = useStore();
  const { user } = authStore;
  const navigate = useNavigate();
  const { uiStore } = useStore();
  const language = uiStore.locale;

  // Location state - values stored as display names
  const [prefecture, setPrefecture] = useState("");
  const [selectedPrefectureCode, setSelectedPrefectureCode] = useState<
    number | undefined
  >(undefined);
  const [city, setCity] = useState("");
  const [selectedCitySlug, setSelectedCitySlug] = useState<string | undefined>(
    undefined,
  );
  const [ward, setWard] = useState("");

  // Fetch location data from API using useGeoLocations hook
  const { prefectures, cities, locations } = useGeoLocations({
    lang: language,
    prefectureCode: selectedPrefectureCode,
    citySlug: selectedCitySlug,
  });

  // Ensure arrays are always defined (defensive check for MobX state)
  const safePrefectures = Array.isArray(prefectures) ? prefectures : [];
  const safeCities = Array.isArray(cities) ? cities : [];
  const safeLocations = Array.isArray(locations) ? locations : [];

  // Convert API data to CustomSelect options format
  const prefectureOptions = safePrefectures.map((p) => ({
    value: p.code,
    label:
      language === "ja"
        ? p.name.ja
        : language === "ru"
          ? p.name.ru || p.name.en
          : p.name.en,
  }));

  const cityOptions = safeCities.map((c) => ({
    value: c.city_slug,
    label:
      language === "ja"
        ? c.name.ja
        : language === "ru"
          ? c.name.ru || c.name.en
          : c.name.en,
  }));

  const wardOptions = safeLocations.map((l) => ({
    value: l.name.en,
    label:
      language === "ja"
        ? l.name.ja
        : language === "ru"
          ? l.name.ru || l.name.en
          : l.name.en,
  }));

  // Other search form state
  const [moveInDate, setMoveInDate] = useState<Date | null>(null);
  const [occupants, setOccupants] = useState(1);
  const [rentMax, setRentMax] = useState<number>(200000);
  const [roomLayout, setRoomLayout] = useState<string[]>([]);

  // Handle prefecture change - cascade clear city and ward
  const handlePrefectureChange = (value: string | number) => {
    const code = Number(value);
    const selected = safePrefectures.find((p) => p.code === code);
    if (selected) {
      setPrefecture(selected.name.en);
      setSelectedPrefectureCode(code);
    } else {
      setPrefecture("");
      setSelectedPrefectureCode(undefined);
    }
    // Clear dependent fields
    setCity("");
    setSelectedCitySlug(undefined);
    setWard("");
  };

  // Handle city change - cascade clear ward
  const handleCityChange = (value: string | number) => {
    const slug = String(value);
    const selected = safeCities.find((c) => c.city_slug === slug);
    if (selected) {
      setCity(selected.name.en);
      setSelectedCitySlug(slug);
    } else {
      setCity("");
      setSelectedCitySlug(undefined);
    }
    // Clear dependent field
    setWard("");
  };

  // Handle ward change
  const handleWardChange = (value: string | number) => {
    setWard(String(value));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    // Build URL params from search form
    const params = new URLSearchParams();

    // Add location params (always include prefecture at minimum)
    if (prefecture) params.set("prefecture", prefecture);
    if (city) params.set("city", city);
    if (ward) params.set("ward", ward);

    // Add occupants if more than 1 (affects room size recommendations)
    if (occupants > 1) params.set("occupants", String(occupants));

    // Add move-in date if specified
    if (moveInDate) {
      const dateStr = moveInDate.toISOString().split("T")[0];
      if (dateStr) params.set("moveInDate", dateStr);
    }

    // Add rent budget (only if less than max)
    if (rentMax < 500000) {
      params.set("rentMax", String(rentMax));
    }

    // Add room layout if specified
    if (roomLayout.length > 0) {
      params.set("roomLayout", roomLayout.join(","));
    }

    // Navigate to overview page with filters
    const queryString = params.toString();
    const overviewPath = AppRoutes.overview || "/overview";
    navigate(queryString ? `${overviewPath}?${queryString}` : overviewPath);
  };

  const today = new Date();

  return (
    <BasePage
      title="Home"
      description="Find your perfect apartment in Japan"
      maxWidth="full"
      background="gradient"
      withAnimatedBlobs
      headerVariant="scroll-aware"
      headerProps={{
        rightContent: (
          <Box className="flex items-center gap-4">
            <LanguageSwitcher />
            {user ? (
              <Link to={AppRoutes.dashboard}>
                <Button variant="primary" size="sm">
                  {t("dashboard")}
                </Button>
              </Link>
            ) : (
              <Link to={AppRoutes.login}>
                <Button variant="primary" size="sm">
                  {t("signIn")}
                </Button>
              </Link>
            )}
          </Box>
        ),
      }}
    >
      <Box className="max-w-7xl 2xl:max-w-[1600px] 3xl:max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12">
        {/* Hero Content */}
        <Box className="text-center mb-12">
          <Text variant="h1" className="text-4xl sm:text-5xl lg:text-6xl mb-4">
            {t("homeHeroTitle")}
          </Text>
          <Text
            variant="bodyLarge"
            color="secondary"
            className="max-w-2xl mx-auto"
          >
            {t("homeHeroSubtitle")}
          </Text>
        </Box>

        {/* Search Card */}
        <Box className="max-w-5xl mx-auto">
          <Box
            as="form"
            onSubmit={handleSearch}
            className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8"
          >
            {/* First Row: Prefecture, City, Location */}
            <Box className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              {/* Prefecture */}
              <Box>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("searchPrefecture")}
                </label>
                <Select
                  value={selectedPrefectureCode || ""}
                  onChange={handlePrefectureChange}
                  options={prefectureOptions}
                  placeholder={t("searchSelectPrefecture")}
                  icon={<Map className="w-5 h-5 text-gray-400" />}
                />
              </Box>

              {/* City */}
              <Box>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("searchCity")}
                </label>
                <Select
                  value={selectedCitySlug || ""}
                  onChange={handleCityChange}
                  options={cityOptions}
                  placeholder={t("searchSelectCity")}
                  icon={<Building className="w-5 h-5 text-gray-400" />}
                  disabled={!selectedPrefectureCode}
                />
              </Box>

              {/* Location (Ward) */}
              <Box>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("searchLocation")}
                </label>
                <Select
                  value={ward}
                  onChange={handleWardChange}
                  options={wardOptions}
                  placeholder={t("searchSelectArea")}
                  icon={<MapPin className="w-5 h-5 text-gray-400" />}
                  disabled={!selectedCitySlug}
                />
              </Box>
            </Box>

            {/* Second Row: Move-in Date, Monthly Budget, Occupants (3 cols) */}
            <Box className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              {/* Move-in Date */}
              <Box>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("searchMoveIn")}
                </label>
                <Box className="relative">
                  <Box className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <Calendar className="w-5 h-5 text-gray-400" />
                  </Box>
                  <DatePicker
                    selected={moveInDate}
                    onChange={(date: Date | null) => setMoveInDate(date)}
                    minDate={today}
                    placeholderText={t("searchMoveInPlaceholder")}
                    dateFormat="MMM d, yyyy"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg hover:border-gray-400 cursor-pointer"
                    calendarClassName="apaato-calendar"
                  />
                </Box>
              </Box>

              {/* Monthly Rent Budget */}
              <Box>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("searchRentBudget")}
                </label>
                <Box className="relative flex items-center">
                  <Box className="absolute left-0 pl-3 flex items-center pointer-events-none">
                    <Banknote className="w-5 h-5 text-gray-400" />
                  </Box>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={rentMax.toLocaleString()}
                    onChange={(e) => {
                      const num = Number(e.target.value.replace(/[^0-9]/g, ""));
                      if (!isNaN(num)) setRentMax(Math.min(num, 500000));
                    }}
                    className="w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg hover:border-gray-400 focus:border-red-500 outline-none transition-all duration-200 shadow-none focus:shadow-[inset_0_0_0_1px_#ef4444]"
                    placeholder="200,000"
                  />
                  <Box className="absolute right-0 inset-y-0 flex items-center">
                    <Text
                      as="span"
                      variant="body"
                      className="text-gray-500 text-sm mr-1"
                    >
                      ¥/mo
                    </Text>
                    <Box className="flex flex-col border-l border-gray-300 h-full">
                      <button
                        type="button"
                        onClick={() =>
                          setRentMax(Math.min(rentMax + 10000, 500000))
                        }
                        className="flex-1 px-2 hover:bg-gray-100 flex items-center justify-center border-b border-gray-300 rounded-tr-lg transition-colors"
                      >
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setRentMax(Math.max(rentMax - 10000, 0))}
                        className="flex-1 px-2 hover:bg-gray-100 flex items-center justify-center rounded-br-lg transition-colors"
                      >
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      </button>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Occupants */}
              <Box>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("searchOccupants")}
                </label>
                <Select
                  value={occupants}
                  onChange={(val) => setOccupants(Number(val))}
                  options={OCCUPANT_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label:
                      opt.value === 5
                        ? `${opt.label} ${t("searchPeople")}`
                        : `${opt.label} ${opt.value === 1 ? t("searchPerson") : t("searchPeople")}`,
                  }))}
                  icon={<Users className="w-5 h-5 text-gray-400" />}
                />
              </Box>
            </Box>

            {/* Third Row: Room Layout (full width) */}
            <Box className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("searchRoomLayout")}
              </label>
              <Box className="flex flex-wrap gap-2">
                {ROOM_LAYOUTS.map((layout) => {
                  const isSelected = roomLayout.includes(layout.value);
                  return (
                    <Chip
                      key={layout.value}
                      selected={isSelected}
                      onClick={() => {
                        if (isSelected) {
                          setRoomLayout(
                            roomLayout.filter((v) => v !== layout.value),
                          );
                        } else {
                          setRoomLayout([...roomLayout, layout.value]);
                        }
                      }}
                    >
                      {layout.value}
                    </Chip>
                  );
                })}
              </Box>
            </Box>

            {/* Search Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              className="text-lg"
            >
              <Search className="w-5 h-5 mr-1" />
              {t("searchButton")}
            </Button>
          </Box>
        </Box>

        {/* Features Grid */}
        <Box className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl 2xl:max-w-6xl 3xl:max-w-7xl mx-auto">
          <Box className="text-center p-6">
            <Box className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Home className="w-8 h-8 text-red-600" />
            </Box>
            <Text variant="h5" className="mb-2">
              {t("featureQualityTitle")}
            </Text>
            <Text variant="body" color="secondary">
              {t("featureQualityDesc")}
            </Text>
          </Box>

          <Box className="text-center p-6">
            <Box className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-red-600" />
            </Box>
            <Text variant="h5" className="mb-2">
              {t("featureTrustedTitle")}
            </Text>
            <Text variant="body" color="secondary">
              {t("featureTrustedDesc")}
            </Text>
          </Box>

          <Box className="text-center p-6">
            <Box className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <HeartHandshake className="w-8 h-8 text-red-600" />
            </Box>
            <Text variant="h5" className="mb-2">
              {t("featureSupportTitle")}
            </Text>
            <Text variant="body" color="secondary">
              {t("featureSupportDesc")}
            </Text>
          </Box>
        </Box>

        {/* Philosophy Section */}
        <Box className="mt-20 max-w-5xl 2xl:max-w-6xl 3xl:max-w-7xl mx-auto">
          <Card
            variant="default"
            padding="lg"
            className="shadow-lg border-gray-100"
          >
            <Box className="text-center mb-8">
              <Box className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-4">
                <Sparkles className="w-7 h-7 text-red-600" />
              </Box>
              <Text
                variant="h2"
                className="text-2xl sm:text-3xl 2xl:text-4xl mb-3"
              >
                {t("philosophyTitle")}
              </Text>
              <Text
                variant="bodyLarge"
                color="secondary"
                className="max-w-2xl mx-auto"
              >
                {t("philosophySubtitle")}
              </Text>
            </Box>

            <Box className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-6 2xl:gap-8">
              {/* No Hidden Fees */}
              <Box className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-red-50/50 transition-colors">
                <Box className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <Eye className="w-5 h-5 text-red-600" />
                </Box>
                <Box>
                  <Text variant="h6" className="mb-1">
                    {t("philosophyNoFeesTitle")}
                  </Text>
                  <Text variant="body" className="text-sm text-gray-600">
                    {t("philosophyNoFeesDesc")}
                  </Text>
                </Box>
              </Box>

              {/* Simple Interface */}
              <Box className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-red-50/50 transition-colors">
                <Box className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <Sparkles className="w-5 h-5 text-red-600" />
                </Box>
                <Box>
                  <Text variant="h6" className="mb-1">
                    {t("philosophySimpleTitle")}
                  </Text>
                  <Text variant="body" className="text-sm text-gray-600">
                    {t("philosophySimpleDesc")}
                  </Text>
                </Box>
              </Box>

              {/* Quality Listings */}
              <Box className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-red-50/50 transition-colors">
                <Box className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <FileText className="w-5 h-5 text-red-600" />
                </Box>
                <Box>
                  <Text variant="h6" className="mb-1">
                    {t("philosophyQualityTitle")}
                  </Text>
                  <Text variant="body" className="text-sm text-gray-600">
                    {t("philosophyQualityDesc")}
                  </Text>
                </Box>
              </Box>

              {/* Verified Realtors */}
              <Box className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-red-50/50 transition-colors">
                <Box className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <BadgeCheck className="w-5 h-5 text-red-600" />
                </Box>
                <Box>
                  <Text variant="h6" className="mb-1">
                    {t("philosophyVerifiedTitle")}
                  </Text>
                  <Text variant="body" className="text-sm text-gray-600">
                    {t("philosophyVerifiedDesc")}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Disclaimer */}
        <Box className="mt-16 max-w-2xl mx-auto">
          <Card
            variant="default"
            padding="md"
            className="bg-amber-50 border-amber-200 text-center"
          >
            <Text variant="body" className="text-amber-800 text-sm">
              <Text as="span" weight="bold">
                🚧 {t("disclaimerTitle")}
              </Text>
              <br />
              {t("disclaimerBody")}
            </Text>
          </Card>
        </Box>
      </Box>
    </BasePage>
  );
});
